import { useCallback, useEffect } from "react";

/**
 * Keeps `touchstart` from bubbling past the element.
 *
 * The VOD player toggles its control bar on every touch that reaches its container (native
 * listener on an ancestor of the players). Controls that live inside that container — layout
 * selector, strip tiles, pagination, the tiles' topbar buttons — must not count as "a tap on
 * the video". A React `onTouchStart` with `stopPropagation` would be too late: React listens
 * at the root, after the player's native listener has already fired, so the stop has to be a
 * native listener on the element itself. Clicks are left alone at this level, so the React
 * `onClick` handlers inside keep working — see `useIsolateClick` for those.
 */
export const useIsolateTouch = <T extends HTMLElement>(ref: React.RefObject<T>) => {
  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    const stop = (e: TouchEvent) => e.stopPropagation();
    element.addEventListener("touchstart", stop, { passive: true });
    return () => element.removeEventListener("touchstart", stop);
  }, [ref]);
};

/**
 * Keeps clicks from bubbling past the element — at React level: the handler it returns goes on
 * the element's `onClick`.
 *
 * React level is the right one here. The click must keep reaching React's root listener for the
 * controls inside to get their own `onClick`; a native stop on the element, like the touch one,
 * would starve them. What must not fire is the React `onClick` of what is around — a strip tile
 * selecting itself on click, on mobile — and React's own propagation stops right here.
 */
export const useIsolateClick = () => useCallback((e: React.MouseEvent) => e.stopPropagation(), []);
